import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { InputWrap, InputDropdown, InputDropdownWrap, DropDownWrap, InputValue, LabelWrapper, Error } from "./styled";
import { Label, useThemeUI } from "theme-ui";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Flex } from "theme-ui";

import { CalendarIcon, ChevronIcon } from "components/svg";

const SelectDate = ({
  label,
  name = "date",
  value,
  description,
  autoComplete,
  minDate,
  maxDate,
  required,
  disabled = false,
  startOfDay = true,
  showTime = false,
  $customStyles,
  $errors,
  $responseErrors,
  $removeMargin,
  onChange,
  usePortal = false,
}: {
  label?: string | React.ReactNode;
  name?: string;
  value?: string | number;
  description?: string;
  autoComplete?: string;
  minDate?: number;
  maxDate?: number;
  required?: boolean;
  disabled?: boolean;
  startOfDay?: boolean;
  showTime?: boolean;
  $customStyles?: any;
  $errors?: any;
  $responseErrors?: any;
  $removeMargin?: boolean;
  onChange?: (e?: any) => void;
  usePortal?: boolean;
}) => {
  const themeContext = useThemeUI();
  const { theme } = themeContext;
  const [borderError, setBorderError] = useState(false);
  const [active, setActive] = useState(false);

  const datePickerRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const typingTimeoutRef = useRef<any>(null);

  const [inputLabel, setInputLabel] = useState("");
  const [dateValue, setDateValue] = useState<any>(value ?? null);
  // Portal position. `top` OR `bottom` is set (never both) so the
  // calendar can flip above the field when there isn't room below it.
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });

  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    // react-datepicker's inline calendar is ~310px tall with our custom
    // header. If the space below the field can't hold it but the space
    // above can, open upward so a field low in the viewport (or low in a
    // scrolled modal) doesn't get its calendar clipped off-screen.
    const CALENDAR_HEIGHT = 320;
    const GAP = 10;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < CALENDAR_HEIGHT + GAP && spaceAbove > spaceBelow;
    if (openUp) {
      setDropdownPosition({
        bottom: window.innerHeight - rect.top + GAP,
        left: rect.left,
      });
    } else {
      setDropdownPosition({
        top: rect.bottom + GAP,
        left: rect.left,
      });
    }
  }, []);

  const setSelectValue = async function (date: any) {
    if (disabled) return true;

    const momentDate = startOfDay ? moment(date).startOf("day").unix() : moment(date).endOf("day").unix();
    if ((!minDate || minDate <= momentDate) && (!maxDate || maxDate >= momentDate)) {
      setDateValue(momentDate);
      if (typeof onChange === "function") {
        onChange(momentDate);
      }
    }
  };

  const inputChange = (e?: any) => {
    setInputLabel(e.target.value);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      try {
        const newDate = moment(e.target.value).toDate();
        if (newDate) {
          setSelectValue(newDate);
          datePickerRef?.current?.setOpen(false);
          setTimeout(() => datePickerRef?.current?.setOpen(true), 500);
        }
      } catch (e: any) {}
    }, 1000);
  };

  const openDropdown = () => {
    if (disabled) return;
    setActive(true);
    if (usePortal) {
      updateDropdownPosition();
    }
  };

  useEffect(() => {
    if (usePortal && active) {
      updateDropdownPosition();
    }
  }, [usePortal, active, updateDropdownPosition]);

  useEffect(() => {
    if (!usePortal || !active) return;

    const handleScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [usePortal, active, updateDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const wrapperEl = wrapperRef.current;
      const portalEl = portalContainerRef.current;
      if (wrapperEl && wrapperEl.contains(event.target)) {
        return;
      }
      if (usePortal && portalEl && portalEl.contains(event.target)) {
        return;
      }
      setActive(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [usePortal]);

  useEffect(() => {
    setDateValue(value);
  }, [value]);

  useEffect(() => {
    setInputLabel(dateValue ? moment.unix(dateValue).format("L") : "");
  }, [dateValue]);

  useEffect(() => {
    if ($responseErrors || $errors) {
      setBorderError(true);
    } else {
      setBorderError(false);
    }
  }, [$responseErrors, $errors]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const datePicker = (
    <InputDropdown
      theme={theme}
      style={
        usePortal
          ? {
              marginTop: 0,
              width: "fit-content",
              minWidth: "unset",
            }
          : undefined
      }
    >
      <DatePicker
        ref={datePickerRef}
        selected={dateValue ? moment.unix(dateValue).toDate() : undefined}
        minDate={minDate ? moment.unix(minDate).toDate() : undefined}
        maxDate={maxDate ? moment.unix(maxDate).toDate() : undefined}
        openToDate={dateValue ? moment.unix(dateValue).toDate() : moment().toDate()}
        onChange={setSelectValue}
        customInput={<></>}
        disabled={disabled}
        showTimeSelect={showTime}
        inline
        renderCustomHeader={({
          date,
          decreaseYear,
          increaseYear,
          decreaseMonth,
          increaseMonth,
        }) => (
          <div className="react-datepicker__custom-year-selector">
            <span className="react-datepicker__current-month">
              {date.toLocaleDateString("en-US", { year: "numeric", month: "long" })}
            </span>
            <div className="react-datepicker__navigation">
              <span
                className="react-datepicker__navigation-icon react-datepicker__navigation-icon-double react-datepicker__navigation-icon--previous"
                onClick={decreaseYear}
              >
                {"<<"}
              </span>
              <span
                className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"
                onClick={decreaseMonth}
              >
                {"<"}
              </span>
              <span
                className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next"
                style={{ top: "-2px" }}
                onClick={increaseMonth}
              >
                {">"}
              </span>
              <span
                className="react-datepicker__navigation-icon react-datepicker__navigation-icon-double react-datepicker__navigation-icon--next"
                style={{ top: "-2px" }}
                onClick={increaseYear}
              >
                {">>"}
              </span>
            </div>
          </div>
        )}
      />
    </InputDropdown>
  );

  return (
    <InputWrap $errors={borderError} sx={$customStyles} disabled={disabled} ref={wrapperRef} $removeMargin={$removeMargin}>
      {label && (
        <LabelWrapper>
          <Label>{label}</Label>
          {required ? <span>*</span> : ""}
        </LabelWrapper>
      )}
      <DropDownWrap>
        <InputValue onClick={openDropdown}>
          <Flex
            sx={{
              background: theme?.colors?.inputOutline,
              padding: "0 8px",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              borderRight: `1px solid ${theme?.colors?.mdgrey}`,
            }}
          >
            <CalendarIcon fill={theme?.colors?.dkgrey} />
          </Flex>
          <input onChange={(e) => inputChange(e)} value={inputLabel} />
          {/* <ChevronIcon fill={active ? theme?.colors?.base_800 : theme?.colors?.base_300} transform={"rotate(-90deg)"}  />        */}
        </InputValue>
        {usePortal
          ? active &&
            createPortal(
              <div
                ref={portalContainerRef}
                style={{
                  position: "fixed",
                  ...(dropdownPosition.bottom != null
                    ? { bottom: dropdownPosition.bottom }
                    : { top: dropdownPosition.top }),
                  left: dropdownPosition.left,
                  zIndex: 9999,
                  display: "inline-block",
                }}
              >
                {datePicker}
              </div>,
              document.body,
            )
          : (
            <InputDropdownWrap $active={active}>{datePicker}</InputDropdownWrap>
          )}
      </DropDownWrap>
      {description && (
        <p>
          <small>{description}</small>
        </p>
      )}
      {$errors && <Error>{$errors}</Error>}
    </InputWrap>
  );
};

export default SelectDate;
